import React, { useEffect } from 'react'
import { Sector } from '../../../models/Common'
import { Box, CircularProgress, ITheme, Link, Typography, useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../store/reducers/store'
import { loadSectors } from '../../../store/actions/createPortfolio'
import { SectorAffectation } from '../../../models/Portfolio'
import { LeoImagesSectorsPath } from '../../../resources/api-constants'
import { Utilities } from '../../../utility/utils'
import CloseIcon from '@mui/icons-material/Close';
import ProportionSelector from './ProportionSelector'
import { useTranslation } from 'react-i18next'

export interface IIndustrySelectorProps {
    value: SectorAffectation[];
    onSectorsChanged: (sectors: SectorAffectation[]) => void;
}

const IndustrySelector: React.FC<IIndustrySelectorProps> = ({ onSectorsChanged, value }) => {
    const theme = useTheme() as ITheme;
    const dispatch = useAppDispatch();
    const sectors = useAppSelector(state => state.createPortfolio.sectors);
    const { t } = useTranslation();

    useEffect(() => {
        if (sectors === undefined) {
            dispatch(loadSectors());
        }
    }, [sectors, dispatch]);

    if (sectors === undefined) {
        return <CircularProgress />
    }

    const onSectorSelected = (id: number): void => {
        const selection = [...value];
        const existingIndex = selection.findIndex(i => i.id === id);
        if (existingIndex !== -1) {
            selection.splice(existingIndex, 1);
        } else {
            const sector = sectors.find(s => s.id === id);
            if (sector) {
                selection.push({ ...sector, proportion: 0 })
            }
        }
        onSectorsChanged(selection);
    }

    const onProportionChanged = (id: number, proportion: number): void => {
        const selection = [...value];
        let total = selection.reduce((a, b) => a + b.proportion, 0);
        const existingIndex = selection.findIndex(i => i.id === id);
        if (existingIndex !== -1) {
            let correctedValue = proportion;
            total -= selection[existingIndex].proportion;
            if (total + correctedValue > 1){
                correctedValue = 1 - total;
            }
            selection[existingIndex] = { ...selection[existingIndex], proportion: correctedValue };
        }
        onSectorsChanged(selection);
    }

    const renderSector = (sector: Sector, selection: SectorAffectation | undefined, onClick: (id: number) => void): React.ReactNode => {
        const selected = selection !== undefined;
        return <Box className={`sector-tag ${Utilities.toClassName(sector.name)}`} display={"flex"} justifyContent={"flex-start"}
                alignItems={"center"} key={`sector-${sector.id}`} borderRadius={"4px"} padding={"10px 12px"} gap={"8px"}
                onClick={() => onClick(sector.id)} sx={{ cursor: "pointer", userSelect: "none" }}>
            {sector.icon && <img src={`${LeoImagesSectorsPath}/${sector.icon}`} style={{ width: "20px", height: "20px" }}
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => { e.currentTarget.style.display = 'none'; }} />}
            <Typography fontSize={"13px"} lineHeight={"20px"} fontWeight={"500"}>{sector.name}</Typography>
            {selected && <ProportionSelector value={selection.proportion} onProportionChanged={(val: number) => onProportionChanged(sector.id, val)} />}
            {selected && <Link sx={{ color: theme.palette.text.primary}}><CloseIcon fontSize='small' sx={{ color: theme.palette.text.primary}} /></Link>}
        </Box>
    }
    return (
        <Box display={"flex"} flexDirection={"column"} gap={"24px"}>
            <Typography fontSize={"16px"} fontWeight={"500"} lineHeight={"19.5px"}>{t('CreatePortfolioSelectIndustryLabel')}</Typography>
            <Box display={"flex"} flexWrap={"wrap"} gap={"16px 12px"}>{sectors.map(s => renderSector(s, value.find(v => s.id === v.id), onSectorSelected))}</Box>
        </Box>
    )
}

export default IndustrySelector